import { useState, useRef, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { DatePicker } from "antd";
import dayjs from "dayjs";
import AdminLayout from "../../../components/layout/AdminLayout";
import AdminPageTitle from "@/components/admin/AdminPageTitle";
import MetricCard from "./components/MetricCard";
import AnswerCountTrendChart from "./components/AnswerCountTrendChart";
import {
  getAnswerTrend,
  getPenetration,
  getQuality,
  parsePascalCaseReason,
  type AnswerTrendResponse,
  type PenetrationResponse,
  type QualityResponse,
  type QualityParams,
} from "../../../services/analytics";
import {
  AreaChart,
  Area,
  LineChart,
  Line,
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
} from "recharts";

type QuickRange = "all" | "today" | "month" | "7days" | "30days";
type TrendRangePreset = "today" | "7d" | "custom";

const DONUT_COLORS = ["#3CB05C", "#FF5050", "#EFAB00"]; // 绿色、红色、橙色

const CustomTooltipStyle = {
  fontSize: 12,
  borderRadius: 8,
  border: "1px solid #E5E7EB",
  padding: "6px 10px",
};

// ─── Helpers ─────────────────────────────────────────────────────────────────
function getCurrentNaturalWeekLabel(): string {
  const now = new Date();
  const day = now.getDay(); // 0=Sun, 1=Mon...
  const diffToMonday = day === 0 ? -6 : 1 - day;
  const monday = new Date(now);
  monday.setDate(now.getDate() + diffToMonday);
  const sunday = new Date(monday);
  sunday.setDate(monday.getDate() + 6);
  const fmt = (d: Date) =>
    `${String(d.getMonth() + 1).padStart(2, "0")}/${String(d.getDate()).padStart(2, "0")}`;
  return `${fmt(monday)} – ${fmt(sunday)}`;
}

function computeQualityParams(
  activeRange: QuickRange,
  startDate: string,
  endDate: string,
): QualityParams {
  // 使用本地时区格式化，避免 toISOString() 按 UTC 计算导致日期在本地 0 点未切换
  const fmt = formatLocalDate;
  const today = new Date();
  if (startDate && endDate) return { startTime: startDate, endTime: endDate };
  if (activeRange === "all") return {};
  if (activeRange === "today") {
    const todayStr = fmt(today);
    return { startTime: todayStr, endTime: todayStr };
  }
  if (activeRange === "month") {
    const firstDay = new Date(today.getFullYear(), today.getMonth(), 1);
    return { startTime: fmt(firstDay), endTime: fmt(today) };
  }
  if (activeRange === "7days") {
    const d = new Date(today);
    d.setDate(today.getDate() - 6);
    return { startTime: fmt(d), endTime: fmt(today) };
  }
  if (activeRange === "30days") {
    const d = new Date(today);
    d.setDate(today.getDate() - 29);
    return { startTime: fmt(d), endTime: fmt(today) };
  }
  return {};
}

function formatLocalDate(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

function getTrendPresetRange(preset: Exclude<TrendRangePreset, "custom">): {
  from: string;
  to: string;
} {
  const now = new Date();
  if (preset === "today") {
    const today = formatLocalDate(now);
    return { from: today, to: today };
  }

  const from = new Date(now);
  from.setDate(now.getDate() - 6);
  return { from: formatLocalDate(from), to: formatLocalDate(now) };
}

// ─── InfoTooltip ──────────────────────────────────────────────────────────────
interface InfoTooltipProps {
  text: string;
}

const InfoTooltip = ({ text }: InfoTooltipProps) => {
  const [visible, setVisible] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  return (
    <div
      ref={ref}
      className="relative inline-flex items-center justify-center w-4 h-4 ml-1 flex-shrink-0"
      onMouseEnter={() => setVisible(true)}
      onMouseLeave={() => setVisible(false)}
    >
      <div className="w-4 h-4 flex items-center justify-center cursor-default">
        <i
          className="ri-information-line text-gray-400 hover:text-gray-600 transition-colors"
          style={{ fontSize: 13 }}
        />
      </div>
      {visible && (
        <div
          className="absolute z-50 bottom-full left-1/2 -translate-x-1/2 mb-2 w-56 bg-gray-900 text-white text-xs rounded-lg px-3 py-2 leading-relaxed pointer-events-none"
          style={{ boxShadow: "0 4px 12px rgba(0,0,0,0.18)" }}
        >
          {text}
          <div className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-gray-900" />
        </div>
      )}
    </div>
  );
};

// ─── KPICard ──────────────────────────────────────────────────────────────────
interface KPICardProps {
  cardId: string;
  title: string;
  tooltip: string;
  value: number;
  unit: string;
  target: number;
  comparison: ">" | "<";
  isGood: boolean;
  sparkData: { rate: number }[];
  color: string;
  onTargetLabel?: string;
  belowTargetLabel?: string;
  targetLabel?: string;
  periodLabel?: string;
  className?: string;
  hideTarget?: boolean;
  detailRows?: { label: string; value: string; after?: string }[];
}

const KPICard = ({
  cardId,
  title,
  tooltip,
  value,
  unit,
  target,
  comparison,
  isGood,
  sparkData,
  color,
  onTargetLabel,
  belowTargetLabel,
  targetLabel,
  periodLabel,
  className,
  hideTarget,
  detailRows,
}: KPICardProps) => {
  const gradId = `spark-grad-${cardId}`;
  return (
    <div
      className={`bg-white border border-gray-200 rounded-lg p-4 flex flex-col gap-2 ${className ?? ""}`}
    >
      <div className="flex items-center justify-between">
        <div className="flex items-center">
          <p className="text-xs text-gray-500 font-medium">{title}</p>
          <InfoTooltip text={tooltip} />
        </div>
        {!hideTarget && (
          <span
            className={`text-xs px-2 py-0.5 rounded-full font-semibold whitespace-nowrap ${isGood ? "bg-green-50 text-[#00875A]" : "bg-red-50 text-[#C72B2B]"}`}
          >
            {isGood ? onTargetLabel : belowTargetLabel}
          </span>
        )}
      </div>
      <div className="flex items-end justify-between gap-2">
        <div>
          <div className="text-2xl font-bold" style={{ color: isGood ? color : "#D9338C" }}>
            {value}
            {unit}
          </div>
          {/* {!hideTarget && (
            <div className="text-xs text-gray-400 mt-0.5">
              {targetLabel} {comparison}
              {target}
              {unit}
            </div>
          )} */}
          {detailRows && detailRows.length > 0 && (
            <div className="flex flex-col gap-y-0.5 mt-1">
              {detailRows.map((row, i) => (
                <div key={i} className="text-xs flex items-center gap-1">
                  <span className="text-gray-400">{row.label}</span>
                  <span className="text-gray-700 font-medium">{row.value}</span>
                  {row.after ? <span className="text-gray-400">{row.after}</span> : null}
                </div>
              ))}
            </div>
          )}
          {periodLabel && (
            <div className="flex items-center gap-1 mt-1">
              <i className="ri-calendar-line text-gray-300" style={{ fontSize: 11 }} />
              <span className="text-xs text-gray-400">{periodLabel}</span>
            </div>
          )}
        </div>
        {/* <div className="w-28 h-10 flex-shrink-0">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={sparkData} margin={{ top: 2, right: 2, left: 2, bottom: 2 }}>
              <defs>
                <linearGradient id={gradId} x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor={color} stopOpacity={0.25} />
                  <stop offset="95%" stopColor={color} stopOpacity={0} />
                </linearGradient>
              </defs>
              <Area
                type="monotone"
                dataKey="rate"
                stroke={color}
                strokeWidth={1.5}
                fill={`url(#${gradId})`}
                dot={false}
                isAnimationActive={false}
              />
            </AreaChart>
          </ResponsiveContainer>
        </div> */}
      </div>
    </div>
  );
};

// ─── SectionTitle ─────────────────────────────────────────────────────────────
interface SectionTitleProps {
  title: string;
  tooltip?: string;
}

const SectionTitle = ({ title, tooltip }: SectionTitleProps) => (
  <div className="flex items-center">
    <h2 className="text-sm font-semibold text-gray-800">{title}</h2>
    {tooltip && <InfoTooltip text={tooltip} />}
  </div>
);

// ─── SectionHeader ────────────────────────────────────────────────────────────
interface SectionHeaderProps {
  icon: string;
  label: string;
  badge?: React.ReactNode;
}

const SectionHeader = ({ icon, label, badge }: SectionHeaderProps) => (
  <div className="flex items-center justify-between mb-4">
    <div className="flex items-center gap-2">
      <div className="w-1 h-4 rounded-full" style={{ backgroundColor: "#830051" }} />
      <span className="text-sm font-semibold text-gray-800 flex items-center gap-1.5">
        <i className={`${icon} text-base`} style={{ color: "#830051" }} />
        {label}
      </span>
    </div>
    {badge}
  </div>
);

// ─── Main Page ────────────────────────────────────────────────────────────────
export default function AnalyticsPage() {
  const { t } = useTranslation();
  const [activeRange, setActiveRange] = useState<QuickRange>("all");
  const [penetrationTrendRange, setPenetrationTrendRange] = useState<TrendRangePreset>("7d");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [trendStartDate, setTrendStartDate] = useState(() => {
    const start = new Date();
    start.setDate(start.getDate() - 6);
    return formatLocalDate(start);
  });
  const [trendEndDate, setTrendEndDate] = useState(() => formatLocalDate(new Date()));
  const [penetrationData, setPenetrationData] = useState<PenetrationResponse | null>(null);
  const [answerTrendData, setAnswerTrendData] = useState<AnswerTrendResponse | null>(null);
  const [qualityData, setQualityData] = useState<QualityResponse | null>(null);
  const [penetrationLoading, setPenetrationLoading] = useState(true);
  const [trendLoading, setTrendLoading] = useState(true);
  const [qualityLoading, setQualityLoading] = useState(true);

  const handleQuickSelect = (value: QuickRange) => {
    setActiveRange(value);
    setStartDate("");
    setEndDate("");
  };

  const handleTrendPresetChange = (preset: Exclude<TrendRangePreset, "custom">) => {
    const { from, to } = getTrendPresetRange(preset);
    setPenetrationTrendRange(preset);
    setTrendStartDate(from);
    setTrendEndDate(to);
  };

  const handleTrendDateChange = (from: string, to: string) => {
    setPenetrationTrendRange("custom");

    if (from && to && from <= to) {
      setTrendStartDate(from);
      setTrendEndDate(to);
    }
  };

  // 获取渗透率数据（页面加载时）
  useEffect(() => {
    setPenetrationLoading(true);
    getPenetration({
      // startTime: `${trendStartDate} 00:00:00`,
      // endTime: `${trendEndDate} 23:59:59`,
    })
      .then((data) => setPenetrationData(data))
      .finally(() => setPenetrationLoading(false));
  }, [trendStartDate, trendEndDate]);

  // 获取质量数据（筛选器变化时）
  useEffect(() => {
    // 自定义日期：只有两端都填了才请求
    if ((startDate && !endDate) || (!startDate && endDate)) return;
    setQualityLoading(true);
    const params = computeQualityParams(activeRange, startDate, endDate);
    if (params.startTime) {
      params.startTime = `${params.startTime} 00:00:00`;
    }
    if (params.endTime) {
      params.endTime = `${params.endTime} 23:59:59`;
    }
    getQuality(params)
      .then((data) => setQualityData(data))
      .finally(() => setQualityLoading(false));
  }, [activeRange, startDate, endDate]);

  useEffect(() => {
    if (!trendStartDate || !trendEndDate) return;

    setTrendLoading(true);
    getAnswerTrend({
      startTime: `${trendStartDate} 00:00:00`,
      endTime: `${trendEndDate} 23:59:59`,
    })
      .then((data) => setAnswerTrendData(data))
      .finally(() => setTrendLoading(false));
  }, [trendStartDate, trendEndDate]);

  const quickOptions: { label: string; value: QuickRange }[] = [
    { label: t("common.all"), value: "all" },
    { label: t("common.today"), value: "today" },
    // { label: t("common.thisMonth"), value: "month" },
    { label: t("common.last7Days"), value: "7days" },
    { label: t("common.last30Days"), value: "30days" },
  ];

  // Section 1: 渗透率 KPI 数据
  const activeUserRate = parseFloat(penetrationData?.activeUserRate.rate ?? "0");
  const highEngagementRate = parseFloat(penetrationData?.highEngagementRate.rate ?? "0");
  const activeSparkData = (penetrationData?.activeUserRate.dailyRates ?? []).map((r) => ({
    rate: parseFloat(r),
  }));
  const engagedSparkData = (penetrationData?.highEngagementRate.dailyRates ?? []).map((r) => ({
    rate: parseFloat(r),
  }));

  // 不足 7 天时，在头部补 0
  const padTo7 = (data: { rate: number }[]) =>
    data.length >= 7 ? data : [...data, ...Array(7 - data.length).fill({ rate: 0 })];
  const paddedActiveSparkData = padTo7(activeSparkData);
  const paddedEngagedSparkData = padTo7(engagedSparkData);

  // Section 1: 周趋势图数据
  const weeklyTrendData = useMemo(() => {
    if (!penetrationData) return [];
    return penetrationData.weeklyTrend.weekEndDates.map((date, i) => {
      // const d = new Date(date);
      // const label = `${String(d.getMonth() + 1).padStart(2, "0")}/${String(d.getDate()).padStart(2, "0")}`;
      return {
        week: date,
        [t("analytics.activeUserRateLabel")]: parseFloat(
          penetrationData.weeklyTrend.activeUserRates[i] ?? "0",
        ),
        [t("analytics.engagementRateLabel")]: parseFloat(
          penetrationData.weeklyTrend.highEngagementRates[i] ?? "0",
        ),
      };
    });
  }, [penetrationData, t]);

  const askCount = answerTrendData?.totalAnswerCount ?? 0;
  const askUserCount = answerTrendData?.totalUserCount ?? 0;

  const askDateRange = useMemo(() => {
    if (!answerTrendData?.startDate || !answerTrendData?.endDate) return "";
    const first = answerTrendData.startDate.slice(5).replace("-", "/");
    const last = answerTrendData.endDate.slice(5).replace("-", "/");
    return `${first} - ${last}`;
  }, [answerTrendData]);

  // Section 2: 满意度甜甜圈数据
  const satisfactionData = [
    { name: t("common.helpful"), value: +(qualityData?.userSatisfaction.usefulCount ?? 0) },
    { name: t("common.unhelpful"), value: +(qualityData?.userSatisfaction.uselessCount ?? 0) },
    { name: t("common.neutral"), value: +(qualityData?.userSatisfaction.noFeedbackCount ?? 0) },
  ];
  const helpfulRate = qualityData?.userSatisfaction.rate ?? "0";
  const helpfulCount = parseFloat(qualityData?.userSatisfaction.usefulCount ?? "0");
  const unhelpfulCount = parseFloat(qualityData?.userSatisfaction.uselessCount ?? "0");
  const neutralCount = parseFloat(qualityData?.userSatisfaction.noFeedbackCount ?? "0");
  const totalAnswer = helpfulCount + unhelpfulCount + neutralCount;

  // Section 2: KPI Cards 数据
  const helpfulRateValue = parseFloat(qualityData?.usefulAnswerRate.rate ?? "0");
  const unhelpfulRateValue = parseFloat(qualityData?.uselessAnswerRate.rate ?? "0");
  const helpfulSparkData = (qualityData?.usefulAnswerRate.dailyRates ?? []).map((r) => ({
    rate: parseFloat(r),
  }));
  const unhelpfulSparkData = (qualityData?.uselessAnswerRate.dailyRates ?? []).map((r) => ({
    rate: parseFloat(r),
  }));

  // Section 2: 响应时间数据（ms → s）
  const p75 = parseFloat(
    ((qualityData?.responseTime.shortTermGoal.p75DurationMs ?? 0) / 1000).toFixed(1),
  );
  const p75Target = parseFloat(
    ((qualityData?.responseTime.shortTermGoal.targetMs ?? 15000) / 1000).toFixed(1),
  );
  const p75Met = qualityData?.responseTime.shortTermGoal.met ?? false;
  const p95 = parseFloat(
    ((qualityData?.responseTime.longTermGoal.p95DurationMs ?? 0) / 1000).toFixed(1),
  );
  const p95Target = parseFloat(
    ((qualityData?.responseTime.longTermGoal.targetMs ?? 15000) / 1000).toFixed(1),
  );
  const p95Met = qualityData?.responseTime.longTermGoal.met ?? false;

  // Section 2: 无用反馈原因分布（PascalCase → 可读文本，客户端计算占比）
  const unhelpfulReasons = useMemo(() => {
    const reasons = qualityData?.uselessFeedbackReasons ?? [];
    const totalCount = reasons.reduce((sum, r) => sum + parseFloat(r.count), 0);
    return reasons
      .map((r) => ({
        reason: parsePascalCaseReason(r.reason),
        count: parseFloat(r.count),
        percentage:
          totalCount > 0 ? parseFloat(((parseFloat(r.count) / totalCount) * 100).toFixed(1)) : 0,
      }))
      .sort((a, b) => b.percentage - a.percentage);
  }, [qualityData]);

  const naturalWeekLabel = getCurrentNaturalWeekLabel();
  const maxPct = Math.max(...unhelpfulReasons.map((r) => r.percentage), 0);

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Page Header */}
        <div>
          <AdminPageTitle title={t("analytics.title")} tooltip={t("analytics.subtitle")} />
        </div>

        {/* ── SECTION 1: AI 使用渗透情况 ─────────────────────────────── */}
        <div className="bg-gray-50 border border-gray-200 rounded-xl p-5 space-y-4">
          <SectionHeader icon="ri-user-heart-line" label={t("analytics.penetrationSection")} />

          <div className="flex items-center justify-between mb-3">
            <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
              Ask Module Usage
            </span>
            <div className="flex items-center gap-2">
              <div className="flex items-center gap-2 flex-wrap">
                <button
                  onClick={() => handleTrendPresetChange("today")}
                  className={`text-xs font-medium px-3 py-1.5 rounded-full transition-colors whitespace-nowrap cursor-pointer ${
                    penetrationTrendRange === "today"
                      ? "text-white"
                      : "text-gray-600 hover:bg-gray-100"
                  }`}
                  style={
                    penetrationTrendRange === "today"
                      ? { backgroundColor: "#830051" }
                      : { backgroundColor: "#f3f4f6" }
                  }
                >
                  Today
                </button>
                <button
                  onClick={() => handleTrendPresetChange("7d")}
                  className={`text-xs font-medium px-3 py-1.5 rounded-full transition-colors whitespace-nowrap cursor-pointer ${
                    penetrationTrendRange === "7d"
                      ? "text-white"
                      : "text-gray-600 hover:bg-gray-100"
                  }`}
                  style={
                    penetrationTrendRange === "7d"
                      ? { backgroundColor: "#830051" }
                      : { backgroundColor: "#f3f4f6" }
                  }
                >
                  Last 7 Days
                </button>
                <div className="w-px h-5 bg-gray-200 mx-1" />
                <div className="flex items-center gap-1.5">
                  <input
                    type="date"
                    value={trendStartDate}
                    max={trendEndDate || undefined}
                    onChange={(e) => {
                      handleTrendDateChange(e.target.value, trendEndDate);
                    }}
                    className="text-xs text-gray-600 border border-gray-200 rounded-md px-2 py-1.5 w-32 cursor-pointer focus:outline-none"
                  />
                  <span className="text-xs text-gray-400">-</span>
                  <input
                    type="date"
                    value={trendEndDate}
                    min={trendStartDate || undefined}
                    onChange={(e) => {
                      handleTrendDateChange(trendStartDate, e.target.value);
                    }}
                    className="text-xs text-gray-600 border border-gray-200 rounded-md px-2 py-1.5 w-32 cursor-pointer focus:outline-none"
                  />
                </div>
              </div>
            </div>
          </div>
          {penetrationLoading || trendLoading ? (
            <div className="flex justify-center py-12">
              <i className="ri-loader-4-line animate-spin text-2xl text-gray-400" />
            </div>
          ) : (
            <>
              <div className="flex gap-4 mb-4">
                <div className="flex flex-col gap-3 w-72 shrink-0">
                  <MetricCard
                    module="Ask"
                    label="Answer Count"
                    value={askCount.toLocaleString()}
                    dateRange={askDateRange}
                    tooltip="Total number of Ask answers in selected period"
                    compact
                    className="flex-1 justify-center"
                  />
                  <MetricCard
                    module="Ask"
                    label="User Count"
                    value={askUserCount.toLocaleString()}
                    dateRange={askDateRange}
                    tooltip="Unique Ask users in selected period"
                    compact
                    className="flex-1 justify-center"
                  />
                </div>
                <div className="flex-1 min-w-0">
                  <AnswerCountTrendChart data={answerTrendData?.daily ?? []} />
                </div>
              </div>
              <div className="flex items-center justify-between mb-3">
                <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
                  User Penetration Depth
                </span>
              </div>
              <div className="grid grid-cols-12 gap-4">
                {/* 2 KPI Cards */}
                <div className="col-span-4 space-y-3">
                  <KPICard
                    cardId="active"
                    title={t("analytics.activeUserRate")}
                    tooltip={t("analytics.activeUserTooltipNew")}
                    value={activeUserRate}
                    unit="%"
                    target={50}
                    comparison=">"
                    isGood={activeUserRate > 50}
                    sparkData={paddedActiveSparkData}
                    color="#D9338C"
                    onTargetLabel={t("common.onTarget")}
                    belowTargetLabel={t("common.belowTarget")}
                    targetLabel={t("analytics.share")}
                    periodLabel={naturalWeekLabel}
                    detailRows={[
                      {
                        label: "Eligible Users",
                        value: penetrationData?.highEngagementRate?.eligibleUsers,
                        after: `of ${penetrationData?.highEngagementRate?.totalUsers} users (${parseInt(penetrationData?.highEngagementRate?.totalUsers) - parseInt(penetrationData?.highEngagementRate?.eligibleUsers)} excluded)`,
                      },
                      {
                        label: "Weekly Active (≥3)",
                        value: penetrationData?.activeUserRate?.qualifiedUsers,
                      },
                    ]}
                  />
                  <KPICard
                    cardId="engaged"
                    title={t("analytics.highlyEngagedRate")}
                    tooltip={t("analytics.highlyEngagedTooltipNew")}
                    value={highEngagementRate}
                    unit="%"
                    target={20}
                    comparison=">"
                    isGood={highEngagementRate > 20}
                    sparkData={paddedEngagedSparkData}
                    color="#D9338C"
                    onTargetLabel={t("common.onTarget")}
                    belowTargetLabel={t("common.belowTarget")}
                    targetLabel={t("analytics.share")}
                    periodLabel={naturalWeekLabel}
                    detailRows={[
                      {
                        label: "Eligible Users",
                        value: penetrationData?.highEngagementRate?.eligibleUsers,
                        after: `of ${penetrationData?.highEngagementRate?.totalUsers} users (${parseInt(penetrationData?.highEngagementRate?.totalUsers) - parseInt(penetrationData?.highEngagementRate?.eligibleUsers)} excluded)`,
                      },
                      {
                        label: "Weekly Active (≥10)",
                        value: penetrationData?.highEngagementRate?.qualifiedUsers,
                      },
                    ]}
                  />
                </div>

                {/* Weekly Trend Chart */}
                <div className="col-span-8 bg-white border border-gray-200 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-3">
                    <SectionTitle
                      title={t("analytics.weeklyPenetrationTrend")}
                      tooltip={t("analytics.weeklyPenetrationTrendTooltip")}
                    />
                    <div className="flex items-center gap-4">
                      <div className="flex items-center gap-1.5">
                        <div className="w-5 h-0.5" style={{ backgroundColor: "#830051" }} />
                        <span className="text-xs text-gray-500">
                          {t("analytics.activeUserRateLabel")}
                        </span>
                      </div>
                      <div className="flex items-center gap-1.5">
                        <svg width="20" height="4">
                          <line
                            x1="0"
                            y1="2"
                            x2="20"
                            y2="2"
                            stroke="#00875A"
                            strokeWidth="2"
                            strokeDasharray="4 2"
                          />
                        </svg>
                        <span className="text-xs text-gray-500">
                          {t("analytics.engagementRateLabel")}
                        </span>
                      </div>
                    </div>
                  </div>
                  <ResponsiveContainer width="100%" height={200}>
                    <LineChart
                      data={weeklyTrendData}
                      margin={{ top: 5, right: 8, left: -18, bottom: 0 }}
                    >
                      <XAxis
                        dataKey="week"
                        tick={{ fontSize: 11, fill: "#9CA3AF" }}
                        axisLine={false}
                        tickLine={false}
                      />
                      <YAxis
                        tick={{ fontSize: 11, fill: "#9CA3AF" }}
                        axisLine={false}
                        tickLine={false}
                        domain={[0, "auto"]}
                        unit="%"
                      />
                      <Tooltip
                        contentStyle={CustomTooltipStyle}
                        formatter={(val: number, name) => [`${val}%`, name]}
                      />
                      <Line
                        type="monotone"
                        dataKey={t("analytics.activeUserRateLabel")}
                        stroke="#830051"
                        strokeWidth={2}
                        dot={{ r: 3, fill: "#830051", strokeWidth: 0 }}
                      />
                      <Line
                        type="monotone"
                        dataKey={t("analytics.engagementRateLabel")}
                        stroke="#00875A"
                        strokeWidth={2}
                        dot={{ r: 3, fill: "#00875A", strokeWidth: 0 }}
                        strokeDasharray="4 2"
                      />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </div>
            </>
          )}
        </div>

        {/* ── SECTION 2: AI 运营质量分析 ─────────────────────────────── */}
        <div className="bg-gray-50 border border-gray-200 rounded-xl p-5 space-y-4">
          <SectionHeader
            icon="ri-bar-chart-2-line"
            label={t("analytics.qualitySection")}
            badge={
              <div className="flex items-center gap-2">
                {/* Quick range buttons */}
                <div className="flex items-center bg-white border border-gray-200 rounded-lg p-0.5 gap-0.5">
                  {quickOptions.map((opt) => (
                    <button
                      key={opt.value}
                      onClick={() => handleQuickSelect(opt.value)}
                      className={`px-3 py-1.5 rounded-md text-xs font-medium transition-all cursor-pointer whitespace-nowrap ${
                        activeRange === opt.value
                          ? "text-white shadow-sm"
                          : "text-gray-500 hover:text-gray-800"
                      }`}
                      style={activeRange === opt.value ? { backgroundColor: "#830051" } : {}}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
                {/* Date range */}
                <div className="w-px h-5 bg-gray-300" />
                <div className="flex items-center gap-1.5">
                  <DatePicker
                    format="YYYY-MM-DD"
                    placeholder="Start date"
                    style={{ borderRadius: 8, borderColor: "#e5e7eb", fontSize: 12 }}
                    value={startDate ? dayjs(startDate) : null}
                    disabledDate={(d) => (endDate ? d.isAfter(dayjs(endDate), "day") : false)}
                    onChange={(d) => {
                      setStartDate(d ? d.format("YYYY-MM-DD") : "");
                      setActiveRange("all");
                    }}
                  />
                  <span className="text-gray-400 text-xs">—</span>
                  <DatePicker
                    format="YYYY-MM-DD"
                    placeholder="End date"
                    style={{ borderRadius: 8, borderColor: "#e5e7eb", fontSize: 12 }}
                    value={endDate ? dayjs(endDate) : null}
                    disabledDate={(d) => (startDate ? d.isBefore(dayjs(startDate), "day") : false)}
                    onChange={(d) => {
                      setEndDate(d ? d.format("YYYY-MM-DD") : "");
                      setActiveRange("all");
                    }}
                  />
                </div>
              </div>
            }
          />

          {qualityLoading ? (
            <div className="flex justify-center py-12">
              <i className="ri-loader-4-line animate-spin text-2xl text-gray-400" />
            </div>
          ) : (
            <>
              {/* KPIs + Donut + Response Time */}
              <div className="grid grid-cols-12 gap-4 items-stretch">
                {/* Helpful & Unhelpful KPI Cards */}
                <div className="col-span-4 flex flex-col gap-3">
                  <KPICard
                    cardId="effective"
                    title={t("analytics.helpfulRate")}
                    tooltip={t("analytics.helpfulRateTooltipNew")}
                    value={helpfulRateValue}
                    unit="%"
                    target={95}
                    comparison=">"
                    isGood={helpfulRateValue > 95}
                    sparkData={helpfulSparkData}
                    color="#D9338C"
                    onTargetLabel={t("common.onTarget")}
                    belowTargetLabel={t("common.belowTarget")}
                    targetLabel={t("analytics.share")}
                    className="flex-1"
                    hideTarget={true}
                    detailRows={[
                      {
                        label: "Helpful Answers",
                        value: qualityData?.usefulAnswerRate?.useful,
                      },
                      {
                        label: "Total Answers",
                        value: qualityData?.usefulAnswerRate?.total,
                      },
                    ]}
                  />
                  <KPICard
                    cardId="unhelpful"
                    title={t("analytics.unhelpfulRate")}
                    tooltip={t("analytics.unhelpfulRateTooltipNew")}
                    value={unhelpfulRateValue}
                    unit="%"
                    target={2}
                    comparison="<"
                    isGood={unhelpfulRateValue < 2}
                    sparkData={unhelpfulSparkData}
                    color="#D9338C"
                    onTargetLabel={t("common.onTarget")}
                    belowTargetLabel={t("common.belowTarget")}
                    targetLabel={t("analytics.share")}
                    className="flex-1"
                    detailRows={[
                      {
                        label: "Unhelpful Answers",
                        value: qualityData?.uselessAnswerRate?.useless,
                      },
                      {
                        label: "Total Answers",
                        value: qualityData?.uselessAnswerRate?.total,
                      },
                    ]}
                  />
                </div>

                {/* Satisfaction Donut */}
                <div className="col-span-4 bg-white border border-gray-200 rounded-lg p-4 flex flex-col">
                  <div className="mb-2">
                    <SectionTitle
                      title={t("analytics.satisfaction")}
                      tooltip={t("analytics.satisfactionTooltipNew")}
                    />
                  </div>
                  <div className="flex flex-col items-center">
                    <div className="relative w-36 h-36">
                      <ResponsiveContainer width="100%" height="100%">
                        <PieChart>
                          <Pie
                            data={satisfactionData}
                            cx="50%"
                            cy="50%"
                            innerRadius={46}
                            outerRadius={66}
                            paddingAngle={3}
                            dataKey="value"
                            startAngle={90}
                            endAngle={-270}
                            isAnimationActive={false}
                          >
                            {satisfactionData.map((_, index) => (
                              <Cell key={index} fill={DONUT_COLORS[index]} />
                            ))}
                          </Pie>
                          <Tooltip contentStyle={CustomTooltipStyle} />
                        </PieChart>
                      </ResponsiveContainer>
                      <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
                        <span className="text-xl font-bold text-gray-900">{helpfulRate}%</span>
                        <span className="text-xs text-gray-400">
                          {t("analytics.satisfactionLabel")}
                        </span>
                      </div>
                    </div>
                    <div className="w-full mt-2 space-y-2">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-1.5">
                          <div
                            className="w-2 h-2 rounded-full"
                            style={{ backgroundColor: "#3CB05C" }}
                          />
                          <span className="text-xs text-gray-500">{t("common.helpful")}</span>
                        </div>
                        <span className="text-sm font-bold text-gray-800">{helpfulCount}</span>
                      </div>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-1.5">
                          <div
                            className="w-2 h-2 rounded-full"
                            style={{ backgroundColor: "#FF5050" }}
                          />
                          <span className="text-xs text-gray-500">{t("common.unhelpful")}</span>
                        </div>
                        <span className="text-sm font-bold text-gray-800">{unhelpfulCount}</span>
                      </div>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-1.5">
                          <div
                            className="w-2 h-2 rounded-full"
                            style={{ backgroundColor: "#EFAB00" }}
                          />
                          <span className="text-xs text-gray-500">{t("common.neutral")}</span>
                        </div>
                        <span className="text-sm font-bold text-gray-800">{neutralCount}</span>
                      </div>
                      <div className="flex items-center justify-between border-t border-gray-100 pt-2">
                        <span className="text-xs text-gray-400">{t("analytics.totalAnswer")}</span>
                        <span className="text-xs font-semibold text-gray-600">{totalAnswer}</span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Performance Card */}
                <div className="col-span-4 bg-white border border-gray-200 rounded-lg p-4 flex flex-col gap-3">
                  <div className="flex items-center justify-between">
                    <SectionTitle
                      title={t("analytics.responseTime")}
                      tooltip={t("analytics.responseTimeTooltip")}
                    />
                  </div>

                  {/* P75 */}
                  <div className="bg-gray-50 rounded-lg px-3 py-3">
                    <div className="flex items-center justify-between mb-1">
                      <div className="flex items-center">
                        <p className="text-xs text-gray-500 font-medium">
                          {t("analytics.shortTermGoal")}
                        </p>
                        <InfoTooltip text={t("analytics.p75Tooltip")} />
                      </div>
                      <span
                        className={`text-xs px-1.5 py-0.5 rounded-full font-medium whitespace-nowrap ${p75Met ? "bg-green-50 text-[#00875A]" : "bg-red-50 text-[#C72B2B]"}`}
                      >
                        {p75Met ? t("common.onTarget") : t("common.belowTarget")}
                      </span>
                    </div>
                    <div className="flex items-end gap-1">
                      <span
                        className="text-2xl font-bold"
                        style={{ color: p75Met ? "#D9338C" : "#D9338C" }}
                      >
                        {p75}
                      </span>
                      <span className="text-sm text-gray-400 mb-0.5">s</span>
                    </div>
                    <p className="text-xs text-gray-400 mt-0.5">
                      {t("analytics.targetWithin")} {p75Target}s &nbsp;·&nbsp;{" "}
                      {t("analytics.p75LatencyDesc")}
                    </p>
                  </div>

                  {/* P95 */}
                  <div className="bg-gray-50 rounded-lg px-3 py-3">
                    <div className="flex items-center justify-between mb-1">
                      <div className="flex items-center">
                        <p className="text-xs text-gray-500 font-medium">
                          {t("analytics.longTermGoal")}
                        </p>
                        <InfoTooltip text={t("analytics.p95Tooltip")} />
                      </div>
                      <span
                        className={`text-xs px-1.5 py-0.5 rounded-full font-medium whitespace-nowrap ${p95Met ? "bg-green-50 text-[#00875A]" : "bg-red-50 text-[#C72B2B]"}`}
                      >
                        {p95Met ? t("common.onTarget") : t("common.belowTarget")}
                      </span>
                    </div>
                    <div className="flex items-end gap-1">
                      <span
                        className="text-2xl font-bold"
                        style={{ color: p95Met ? "#D9338C" : "#D9338C" }}
                      >
                        {p95}
                      </span>
                      <span className="text-sm text-gray-400 mb-0.5">s</span>
                    </div>
                    <p className="text-xs text-gray-400 mt-0.5">
                      {t("analytics.targetWithin")} {p95Target}s &nbsp;·&nbsp;{" "}
                      {t("analytics.p95LatencyDesc")}
                    </p>
                  </div>
                </div>
              </div>

              {/* Unhelpful Reason Distribution */}
              <div className="bg-white border border-gray-200 rounded-lg p-4">
                <div className="mb-3">
                  <SectionTitle
                    title={t("analytics.unhelpfulReasonDist")}
                    tooltip={t("analytics.unhelpfulReasonTooltip")}
                  />
                </div>
                {unhelpfulReasons.length === 0 ? (
                  <div className="flex flex-col items-center justify-center py-10 text-gray-400">
                    <div className="w-10 h-10 flex items-center justify-center mb-2">
                      <i className="ri-inbox-line text-4xl text-gray-300" />
                    </div>
                    <p className="text-sm">{t("common.noData")}</p>
                  </div>
                ) : (
                  <ResponsiveContainer width="100%" height={240}>
                    <BarChart
                      data={unhelpfulReasons}
                      layout="vertical"
                      margin={{ top: 0, right: 12, left: 0, bottom: 0 }}
                      barSize={14}
                    >
                      <XAxis
                        type="number"
                        tick={{ fontSize: 11, fill: "#9CA3AF" }}
                        axisLine={false}
                        tickLine={false}
                        unit="%"
                        domain={[0, Math.ceil(maxPct / 10) * 10]}
                      />
                      <YAxis
                        type="category"
                        dataKey="reason"
                        tick={{ fontSize: 11, fill: "#6B7280" }}
                        axisLine={false}
                        tickLine={false}
                        width={240}
                      />
                      <Tooltip
                        contentStyle={CustomTooltipStyle}
                        formatter={(val: number, name, item) => [
                          `${item.payload.count}`,
                          t("analytics.count"),
                        ]}
                      />
                      <Bar
                        dataKey="percentage"
                        fill="#EFAB00"
                        radius={[0, 4, 4, 0]}
                        isAnimationActive={false}
                      />
                    </BarChart>
                  </ResponsiveContainer>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </AdminLayout>
  );
}
